"use client"

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from "@headlessui/react"
import { Fragment, useEffect, useMemo, useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import ReactCountryFlag from "react-country-flag"

import { StateType } from "@lib/hooks/use-toggle-state"
import { updateLocale } from "@lib/data/locale-actions"
import { Locale } from "@lib/data/locales"
import { t } from "@lib/i18n"
import { isPickerLocale, toPickerLocale } from "@lib/i18n/locales"

type LanguageOption = {
  code: string
  name: string
  localizedName: string
  countryCode: string
}

const getCountryCodeFromLocale = (localeCode: string): string => {
  try {
    const locale = new Intl.Locale(localeCode)
    if (locale.region) {
      return locale.region.toUpperCase()
    }
    const maximized = locale.maximize()
    return maximized.region?.toUpperCase() ?? localeCode.toUpperCase()
  } catch {
    const parts = localeCode.split(/[-_]/)
    return parts.length > 1 ? parts[1].toUpperCase() : parts[0].toUpperCase()
  }
}

const getLocalizedLanguageName = (
  code: string,
  fallbackName: string,
  displayLocale: string = "en-US"
): string => {
  try {
    const displayNames = new Intl.DisplayNames([displayLocale], {
      type: "language",
    })
    return displayNames.of(code) ?? fallbackName
  } catch {
    return fallbackName
  }
}

type LanguageSelectProps = {
  toggleState?: StateType
  locales: Locale[]
  currentLocale: string | null
  /** Compact control for desktop header */
  variant?: "menu" | "header"
}

const LanguageSelect = ({
  toggleState,
  locales,
  currentLocale,
  variant = "menu",
}: LanguageSelectProps) => {
  const [current, setCurrent] = useState<LanguageOption | undefined>(undefined)
  const [isPending, startTransition] = useTransition()
  const router = useRouter()
  const activeLocale = toPickerLocale(currentLocale)

  const options = useMemo(() => {
    return locales
      .filter((locale) => isPickerLocale(locale.code))
      .map((locale) => ({
        code: locale.code,
        name: locale.name,
        localizedName: getLocalizedLanguageName(
          locale.code,
          locale.name,
          activeLocale
        ),
        countryCode: getCountryCodeFromLocale(locale.code),
      }))
  }, [locales, activeLocale])

  useEffect(() => {
    const option = options.find(
      (o) => o.code.toLowerCase() === activeLocale.toLowerCase()
    )
    setCurrent(option ?? options[0])
  }, [options, activeLocale])

  const handleChange = (option: LanguageOption) => {
    startTransition(async () => {
      await updateLocale(option.code)
      toggleState?.close()
      router.refresh()
    })
  }

  if (!options.length) {
    return null
  }

  const selected =
    options.find((o) => o.code.toLowerCase() === activeLocale.toLowerCase()) ||
    options[0]

  if (variant === "header") {
    return (
      <Listbox value={selected} onChange={handleChange} disabled={isPending}>
        <div className="relative">
          <ListboxButton
            className="flex min-h-11 items-center gap-1.5 text-[13px] font-medium text-vx-ink/80 transition-colors hover:text-vx-ink"
            data-testid="header-language-select"
            aria-label={t(activeLocale, "nav.language")}
          >
            {selected.countryCode && (
              /* @ts-ignore */
              <ReactCountryFlag
                svg
                style={{ width: "16px", height: "16px" }}
                countryCode={selected.countryCode}
              />
            )}
            <span>{isPending ? "…" : selected.localizedName}</span>
          </ListboxButton>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions className="absolute right-0 z-[900] mt-1 max-h-60 min-w-[180px] overflow-auto rounded-md border border-vx-line bg-white py-1 text-sm shadow-lg focus:outline-none">
              {options.map((o) => (
                <ListboxOption
                  key={o.code}
                  value={o}
                  className="flex cursor-pointer items-center gap-2 px-3 py-2 text-vx-ink data-[focus]:bg-vx-elevated"
                >
                  {o.countryCode ? (
                    /* @ts-ignore */
                    <ReactCountryFlag
                      svg
                      style={{ width: "16px", height: "16px" }}
                      countryCode={o.countryCode}
                    />
                  ) : null}
                  {o.localizedName}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
    )
  }

  const { state, close } = toggleState!

  return (
    <div>
      <Listbox
        as="span"
        onChange={handleChange}
        value={selected}
        disabled={isPending}
      >
        <ListboxButton className="py-1 w-full">
          <div className="txt-compact-small flex items-start gap-x-2">
            <span>{t(activeLocale, "nav.language")}:</span>
            {current && (
              <span className="txt-compact-small flex items-center gap-x-2">
                {current.countryCode && (
                  /* @ts-ignore */
                  <ReactCountryFlag
                    svg
                    style={{
                      width: "16px",
                      height: "16px",
                    }}
                    countryCode={current.countryCode}
                  />
                )}
                {isPending ? "..." : current.localizedName}
              </span>
            )}
          </div>
        </ListboxButton>
        <div className="flex relative w-full min-w-[320px]">
          <Transition
            show={state}
            as={Fragment}
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={close}
          >
            <ListboxOptions
              className="absolute -bottom-[calc(100%-36px)] left-0 xsmall:left-auto xsmall:right-0 max-h-[442px] overflow-y-scroll z-[900] bg-white drop-shadow-md text-small-regular uppercase text-black no-scrollbar rounded-rounded w-full"
              static
            >
              {options.map((o) => (
                <ListboxOption
                  key={o.code}
                  value={o}
                  className="py-2 hover:bg-gray-200 px-3 cursor-pointer flex items-center gap-x-2"
                >
                  {o.countryCode ? (
                    /* @ts-ignore */
                    <ReactCountryFlag
                      svg
                      style={{
                        width: "16px",
                        height: "16px",
                      }}
                      countryCode={o.countryCode}
                    />
                  ) : (
                    <span style={{ width: "16px", height: "16px" }} />
                  )}
                  {o.localizedName}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Transition>
        </div>
      </Listbox>
    </div>
  )
}

export default LanguageSelect
